import { observer } from 'mobx-react';
import { ChatBotStore } from '../stores/chatBotStore';
import { ChatMessage, ChatMessageType } from '../types';
import { Box, Typography, CircularProgress, Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AndroidIcon from '@material-ui/icons/Android';
import PersonIcon from '@material-ui/icons/Person';
import { MessageContent } from './MessageContent';
import { ComponentRenderer } from './ComponentRenderer';
import { ThumbButtons } from './feedBack';
const useStyles = makeStyles(theme => ({
  messageItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  messageContent: {
    '&:hover $thumbButtonsContainer': {
      opacity: 1,
    },
  },
  fixWidth: {
    maxWidth: '75%',
  },
  avatar: {
    width: 36,
    height: 36,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  userAvatar: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  },
  botAvatar: {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
  },
  messageBubble: {
    padding: theme.spacing(1.5, 2),
    borderRadius: '18px',
    wordWrap: 'break-word',
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  userBubble: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    borderBottomRightRadius: '4px',
    boxShadow: '0 2px 8px rgba(33, 57, 94, 0.3)',
  },
  botBubble: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottomLeftRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${theme.palette.divider}`,
  },
  alertBubble: {
    background: '#FF2E5B1A',
  },
  timestamp: {
    fontSize: '0.7rem',
    color: '#94a3b8',
    marginTop: theme.spacing(0.5),
    fontWeight: 500,
  },
  thumbButtonsContainer: {
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
}));

interface MessageComponentProps {
  message: ChatMessage;
  store: ChatBotStore;
  isFullScreen?: boolean;
}


export const MessageComponent = observer(({ message, store, isFullScreen }: MessageComponentProps) => {
  const classes = useStyles();

  const renderMessageComponent = () => {
    switch (message.sender) {
      default:
        return (<Box
          className={`${classes.messageBubble} ${message.sender === ChatMessageType.USER
            ? classes.userBubble
            : classes.botBubble
            } ${message.sender === 'alert' && classes.alertBubble}`}
        >
          {message.components?.map((component, index) => (
            <ComponentRenderer key={index} component={component} store={store} />
          ))}
          <MessageContent
            text={message.text}
            isStreaming={message.isStreaming}
            onMenuItemClick={(menuItem) => {
              // Send the menu item as a user message
              store.sendMessage(menuItem);
            }}
            streamingIndicator={
              <CircularProgress
                size={12}
                thickness={4}
                style={{
                  marginLeft: '8px',
                  verticalAlign: 'middle',
                  color: 'currentColor',
                  opacity: 0.7
                }}
              />
            }
          />
        </Box>);
    }
  };

  return (
    <Box className={`${classes.messageItem} ${isFullScreen && classes.fixWidth}`}>
      {(message.sender !== ChatMessageType.USER) && (
        <Avatar className={`${classes.avatar} ${classes.botAvatar}`}>
          <AndroidIcon fontSize="small" />
        </Avatar>
      )}
      <Box className={classes.messageContent}>
        {renderMessageComponent()}
        <Box display='flex' justifyContent={message.sender === ChatMessageType.USER ? 'flex-end' : 'space-between'}>
          <Typography
            className={classes.timestamp}
          >
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
          {message.sender === ChatMessageType.BOT && (
            <Box className={classes.thumbButtonsContainer}>
              <ThumbButtons store={store} messageId={message.id} />
            </Box>
          )}
        </Box>
      </Box>
      {message.sender === ChatMessageType.USER && (
        <Avatar className={`${classes.avatar} ${classes.userAvatar}`}>
          <PersonIcon fontSize="small" />
        </Avatar>
      )}
    </Box>
  );
});

